<script setup lang="ts">
import {
  CircleAlertIcon,
  CircleCheckIcon,
  CircleXIcon,
  InfoIcon,
  XIcon,
} from 'lucide-vue-next'
import { removeToast, toasts } from '~/services/toastService'

const colorByType = {
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    icon: 'text-green-600',
    close: 'text-green-400 hover:text-green-600',
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    icon: 'text-red-600',
    close: 'text-red-400 hover:text-red-600',
  },
  warning: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-800',
    icon: 'text-yellow-600',
    close: 'text-yellow-400 hover:text-yellow-600',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    icon: 'text-blue-600',
    close: 'text-blue-400 hover:text-blue-600',
  },
}

const renderIconBasedOnType = (type: string) => {
  switch (type) {
    case 'error':
      return CircleXIcon
    case 'success':
      return CircleCheckIcon
    case 'warning':
      return CircleAlertIcon
    case 'info':
      return InfoIcon
  }
}
</script>

<template>
  <Teleport to="body">
    <div class="fixed top-[20px] right-[20px] z-[99999] isolate">
      <transition-group name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="relative px-4 py-2 mb-3 min-w-80 max-w-96 rounded-lg border shadow-lg backdrop-blur-sm"
          :class="[
            colorByType[toast.type as keyof typeof colorByType].bg,
            colorByType[toast.type as keyof typeof colorByType].border,
            colorByType[toast.type as keyof typeof colorByType].text,
          ]"
        >
          <!-- Close button -->
          <button
            type="button"
            class="absolute top-2 right-2 p-1 rounded-md transition-colors duration-200"
            :class="colorByType[toast.type as keyof typeof colorByType].close"
            @click="removeToast(toast.id)"
          >
            <component
              :is="XIcon"
              :size="16"
            />
          </button>

          <!-- Content -->
          <div class="pr-8" :class="{ 'flex items-center min-h-[2.5rem]': !toast.message }">
            <!-- Icon and Title -->
            <div class="flex items-center gap-2" :class="{ 'mb-2': toast.message }">
              <div class="flex-shrink-0">
                <component
                  :is="renderIconBasedOnType(toast.type)"
                  :class="colorByType[toast.type as keyof typeof colorByType].icon"
                  :size="20"
                />
              </div>
              <div class="font-medium text-sm leading-5 min-w-0">
                {{ toast.title }}
              </div>
            </div>

            <!-- Message -->
            <div
              v-if="toast.message"
              class="text-sm leading-4 opacity-90 ml-7"
            >
              {{ toast.message }}
              <component
                :is="toast.icon"
                v-if="toast.icon"
                :size="16"
                class="inline ml-1"
              />
            </div>
          </div>

          <!-- Progress bar -->
          <div
            class="absolute bottom-0 left-0 h-1 rounded-b-lg transition-all duration-100 ease-linear"
            :class="colorByType[toast.type as keyof typeof colorByType].icon.replace('text-', 'bg-')"
            :style="{ width: `${toast.progress}%` }"
          />
        </div>
      </transition-group>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-move {
  transition: transform 0.3s ease;
}
</style>
